import styles from './CoachProfilePage.module.css';
import { useUser } from '../../../Context/useUser';
import { useEffect, useRef, useState } from 'react';
import API from '../../../Utils/api';

import Avatar from 'react-avatar';
import { toast } from 'react-toastify';
import { useParams } from 'react-router';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import { FaCreditCard } from 'react-icons/fa';
import { FaLocationDot, FaBuildingColumns } from 'react-icons/fa6';
import { IoNotifications } from 'react-icons/io5';
import { MdOutlineSecurity } from 'react-icons/md';
import { HiOutlineIdentification } from 'react-icons/hi';
import { BsCashCoin } from 'react-icons/bs';
import { BsBank } from 'react-icons/bs';
import { FaRegCreditCard } from 'react-icons/fa6';

import { Badge, Button } from 'react-bootstrap';
import Table from '../../../Components/Table/Table';
import { useForm } from 'react-hook-form';

// The rail settings nav and the main header's current-section label read from this one list.
const SECTIONS = [
    { key: 'profile', label: 'Profile', Icon: HiOutlineIdentification },
    { key: 'security', label: 'Security', Icon: MdOutlineSecurity },
    { key: 'billing', label: 'Billing', Icon: FaCreditCard },
    { key: 'notifications', label: 'Notifications', Icon: IoNotifications }
];

// Fields the profile form edits. A coach's email is always read-only here.
const PROFILE_FIELDS = [
    'title',
    'firstName',
    'lastName',
    'birthYear',
    'gender',
    'phoneNumber',
    'location',
    'swimmingClub'
];

const toProfileDraft = source => Object.fromEntries(PROFILE_FIELDS.map(field => [field, source[field]]));

// Inputs hold strings, the loaded user may hold numbers or null: compare them as the controls show them.
const asControlValue = value => (value === null || value === undefined ? '' : String(value));

const hasDraftChanges = (draft, source) => {
    const savedDraft = toProfileDraft(source);
    return PROFILE_FIELDS.some(field => asControlValue(draft[field]) !== asControlValue(savedDraft[field]));
};

const GENDER_OPTIONS = [
    { value: '', label: 'Select Gender' },
    { value: 'Female', label: 'Female' },
    { value: 'Male', label: 'Male' },
    { value: 'Other', label: 'Other' }
];

export default function CoachProfilePage() {
    const { user, setUser } = useUser();

    const { coachId } = useParams();

    const visitedUser = useVisitingUser(coachId);

    const [viewMode, setViewMode] = useState('profile'); // 'security', 'billing', 'notifications'
    const [currentUser, setCurrentUser] = useState(null);
    const [profileDraft, setProfileDraft] = useState(null);
    const [coachStats, setCoachStats] = useState(null);
    const [isSavingProfile, setIsSavingProfile] = useState(false);

    useEffect(() => {
        setCurrentUser(coachId ? visitedUser : user);
    }, [visitedUser, coachId, user]);

    // The draft is (re)initialised from every loaded or saved user, except that an unsaved draft for the same
    // user survives saves that do not touch it (e.g. notifications); typing only ever touches the draft.
    const draftSource = useRef(null);
    useEffect(() => {
        const previous = draftSource.current;
        draftSource.current = currentUser;
        setProfileDraft(draft =>
            draft && previous && currentUser?._id === previous._id && hasDraftChanges(draft, previous)
                ? draft
                : currentUser
                  ? toProfileDraft(currentUser)
                  : null
        );
    }, [currentUser]);

    // Shared by the profile form and the notifications form; each passes only its own changes.
    // Resolves to the saved user, or null when the save failed.
    const persistUser = async nextUser => {
        try {
            const payload = { ...nextUser };
            if (!payload.gender) {
                delete payload.gender;
            }
            let savedUser;
            if (user._id === currentUser._id) {
                const { data } = await API.updateMyself(payload);
                setUser({ ...user, ...data });
                savedUser = data;
            } else {
                const { data } = await API.updateUser(currentUser._id, payload);
                savedUser = data;
            }
            setCurrentUser(savedUser);
            toast.success('Profile updated.');
            return savedUser;
        } catch (e) {
            toast.error('Failed to save profile.');
            return null;
        }
    };

    const saveProfile = async event => {
        event.preventDefault();
        if (!currentUser?._id) return;
        setIsSavingProfile(true);
        try {
            const savedUser = await persistUser({ ...currentUser, ...profileDraft });
            if (savedUser) setProfileDraft(toProfileDraft(savedUser));
        } finally {
            setIsSavingProfile(false);
        }
    };

    useEffect(() => {
        if (!currentUser?._id) return;
        const fetchStats = async () => {
            try {
                const { data } = await API.getWorkoutStatsByCoachId(currentUser._id);
                setCoachStats(data);
            } catch (e) {
                setCoachStats(null);
            }
        };
        fetchStats();
    }, [currentUser?._id]);

    const paymentColumns = [
        {
            Header: 'Transaction ID',
            accessor: 'Transaction Id'
        },
        { Header: 'Payment Date', accessor: 'date' },
        {
            Header: 'Amount (€)',
            accessor: 'Amount'
        },
        {
            Header: 'Payment Status',
            accessor: 'Status',
            Cell: ({ value }) => (
                <div>
                    <Badge pill bg='success'>
                        Paid
                    </Badge>
                </div>
            )
        }
    ];

    const { register, handleSubmit, reset } = useForm({
        defaultValues: {
            changesToAccount: user?.notifications?.changesToAccount ?? false,
            promotionalOffers: user?.notifications?.promotionalOffers ?? false
        }
    });

    const {
        register: registerPassword,
        handleSubmit: handlePasswordSubmit,
        reset: resetPasswordForm,
        formState: { errors: passwordErrors, isSubmitting }
    } = useForm();

    const onPasswordChange = async ({ currentPassword, newPassword, confirmPassword }) => {
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match.');
            return;
        }
        try {
            await API.updatePassword({
                userId: currentUser._id,
                oldPassword: currentPassword,
                newPassword
            });
            toast.success('Password updated successfully.');
            resetPasswordForm();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Failed to update password.');
        }
    };

    useEffect(() => {
        if (currentUser) {
            reset({
                changesToAccount: currentUser.notifications?.changesToAccount ?? false,
                promotionalOffers: currentUser.notifications?.promotionalOffers ?? false
            });
        }
    }, [currentUser, reset]);

    const onNotificationFormSubmit = data => {
        // Convert checkbox values to booleans
        const updatedNotifications = {
            changesToAccount: !!data.changesToAccount,
            promotionalOffers: !!data.promotionalOffers
        };
        return persistUser({
            ...currentUser,
            notifications: {
                ...currentUser.notifications,
                ...updatedNotifications
            }
        });
    };

    if (!currentUser || !profileDraft) return;

    const updateDraft = field => event => {
        const { value } = event.target;
        setProfileDraft(draft => ({ ...draft, [field]: value }));
    };

    const hasUnsavedChanges = hasDraftChanges(profileDraft, currentUser);

    const draftValue = field => profileDraft[field] ?? '';

    const activeSection = SECTIONS.find(section => section.key === viewMode);

    return (
        <div className={styles.pageWrap}>
            <aside className={styles.leftSide}>
                <div className={styles.identity}>
                    <Avatar
                        name={`${currentUser.firstName} ${currentUser.lastName}`}
                        round={true}
                    />
                    <div className={styles.identityText}>
                        <div className={styles.name}>
                            {currentUser.title} {currentUser.firstName} {currentUser.lastName}
                        </div>
                        <div className={styles.email}>{currentUser.email}</div>
                    </div>
                </div>
                <div className={styles.meta}>
                    <div className={styles.metaRow} title='Location'>
                        <FaLocationDot className={styles.metaIcon} />{' '}
                        {currentUser?.location ?? 'Not Provided'}
                    </div>
                    <div className={styles.metaRow} title='Swimming Club'>
                        <FaBuildingColumns className={styles.metaIcon} />{' '}
                        {currentUser.swimmingClub ?? 'Not Provided'}
                    </div>
                </div>
                <nav className={styles.settingsNav} aria-label='Profile settings'>
                    {SECTIONS.map(({ key, label, Icon }) => (
                        <button
                            key={key}
                            type='button'
                            className={`${styles.navBtn} ${viewMode === key ? styles.active : ''}`}
                            aria-current={viewMode === key ? 'page' : undefined}
                            onClick={() => setViewMode(key)}
                        >
                            <Icon size={18} aria-hidden='true' />
                            <span>{label}</span>
                        </button>
                    ))}
                </nav>
                <div className={styles.about}>
                    <div className={styles.aboutTitle}>About</div>
                    <div className={styles.aboutCntent}>
                        <div className={styles.statRow}>
                            <b>Total Athletes:</b>{' '}
                            <span className={styles.statCount}>{coachStats?.athletes ?? 0}</span>
                        </div>
                        <div className={styles.statRow}>
                            <b>Total Groups:</b>{' '}
                            <span className={styles.statCount}>{coachStats?.groups ?? 0}</span>
                        </div>
                        <div className={styles.statRow}>
                            <b>Total Workout Plans Created:</b>{' '}
                            <span className={styles.statCount}>{coachStats?.programs ?? 0}</span>
                        </div>
                    </div>
                </div>
            </aside>
            <div className={styles.rightSide}>
                <h1 className={styles.srOnly}>{activeSection.label}</h1>
                <div className={styles.selectedMenuItem}>
                    {viewMode === 'profile' && (
                        <form className={styles.profileForm} onSubmit={saveProfile}>
                            <section className={styles.panel} aria-labelledby='profile-personal'>
                                <h2 id='profile-personal' className={styles.groupTitle}>
                                    Personal details
                                </h2>
                                <div className={styles.fieldGrid}>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-title'>
                                            Title
                                        </label>
                                        <input
                                            id='pf-title'
                                            className={styles.control}
                                            type='text'
                                            autoComplete='honorific-prefix'
                                            value={draftValue('title')}
                                            onChange={updateDraft('title')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-firstName'>
                                            First name
                                        </label>
                                        <input
                                            id='pf-firstName'
                                            className={styles.control}
                                            type='text'
                                            autoComplete='given-name'
                                            value={draftValue('firstName')}
                                            onChange={updateDraft('firstName')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-lastName'>
                                            Last name
                                        </label>
                                        <input
                                            id='pf-lastName'
                                            className={styles.control}
                                            type='text'
                                            autoComplete='family-name'
                                            value={draftValue('lastName')}
                                            onChange={updateDraft('lastName')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-birthYear'>
                                            Birth year
                                        </label>
                                        <input
                                            id='pf-birthYear'
                                            className={styles.control}
                                            type='number'
                                            inputMode='numeric'
                                            value={draftValue('birthYear')}
                                            onChange={updateDraft('birthYear')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-gender'>
                                            Gender
                                        </label>
                                        <select
                                            id='pf-gender'
                                            className={styles.control}
                                            value={draftValue('gender')}
                                            onChange={updateDraft('gender')}
                                        >
                                            {GENDER_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </section>
                            <section className={styles.panel} aria-labelledby='profile-contact'>
                                <h2 id='profile-contact' className={styles.groupTitle}>
                                    Contact &amp; club
                                </h2>
                                <div className={styles.fieldGrid}>
                                    <div className={styles.field}>
                                        <span className={styles.fieldLabel}>Email</span>
                                        <span className={styles.readValue}>{currentUser.email}</span>
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-phoneNumber'>
                                            Phone number
                                        </label>
                                        <input
                                            id='pf-phoneNumber'
                                            className={styles.control}
                                            type='tel'
                                            autoComplete='tel'
                                            value={draftValue('phoneNumber')}
                                            onChange={updateDraft('phoneNumber')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-location'>
                                            Location
                                        </label>
                                        <input
                                            id='pf-location'
                                            className={styles.control}
                                            type='text'
                                            value={draftValue('location')}
                                            onChange={updateDraft('location')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-swimmingClub'>
                                            Swimming club
                                        </label>
                                        <input
                                            id='pf-swimmingClub'
                                            className={styles.control}
                                            type='text'
                                            value={draftValue('swimmingClub')}
                                            onChange={updateDraft('swimmingClub')}
                                        />
                                    </div>
                                </div>
                            </section>
                            <div className={styles.actionBar}>
                                <span
                                    className={`${styles.saveStatus} ${hasUnsavedChanges ? styles.dirty : ''}`}
                                    role='status'
                                >
                                    {hasUnsavedChanges ? (
                                        <>
                                            <span className={styles.unsavedDot} aria-hidden='true' />
                                            Unsaved changes
                                        </>
                                    ) : (
                                        'No unsaved changes'
                                    )}
                                </span>
                                <button
                                    type='submit'
                                    className='btn btn-primary'
                                    disabled={!hasUnsavedChanges || isSavingProfile}
                                >
                                    {isSavingProfile ? 'Saving...' : 'Save changes'}
                                </button>
                            </div>
                        </form>
                    )}
                    {viewMode === 'security' && (
                        <div className={styles.securityPage}>
                            <div className={styles.changePasswordArea}>
                                <div className={styles.header}>Change Password</div>
                                <form
                                    className={styles.changePasswordContent + ' form-group'}
                                    onSubmit={handlePasswordSubmit(onPasswordChange)}
                                >
                                    <div className={styles.changePasswordField}>
                                        <label htmlFor='currentPassword'>Current Password</label>
                                        <input
                                            type='password'
                                            className='form-control'
                                            id='currentPassword'
                                            placeholder='Enter current password'
                                            {...registerPassword('currentPassword', {
                                                required: true
                                            })}
                                        />
                                        {passwordErrors.currentPassword && (
                                            <span className={styles.error}>
                                                Current password is required
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.changePasswordField}>
                                        <label htmlFor='newPassword'>New Password</label>
                                        <input
                                            type='password'
                                            className='form-control'
                                            id='newPassword'
                                            placeholder='Enter new password'
                                            {...registerPassword('newPassword', {
                                                required: true,
                                                minLength: 5
                                            })}
                                        />
                                        {passwordErrors.newPassword && (
                                            <span className={styles.error}>
                                                New password is required (min 6 chars)
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.changePasswordField}>
                                        <label htmlFor='confirmPassword'>
                                            Confirm New Password
                                        </label>
                                        <input
                                            type='password'
                                            className='form-control'
                                            id='confirmPassword'
                                            placeholder='Confirm new password'
                                            {...registerPassword('confirmPassword', {
                                                required: true
                                            })}
                                        />
                                        {passwordErrors.confirmPassword && (
                                            <span className={styles.error}>
                                                Please confirm your new password
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        className={`${styles.changePasswordFormButton} btn btn-primary`}
                                        type='submit'
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Submit'}
                                    </button>
                                </form>
                            </div>
                            <div className={styles.disableAccountArea}>
                                <div className={styles.header}>Disable Account</div>
                                <div className={styles.disableAccountContent}>
                                    <div>
                                        Disable your account is a non permanent action and can only
                                        be undone by support. If you are sure you want to disable
                                        your account, select the button below.
                                    </div>
                                    <Button
                                        variant='outline-danger'
                                        onClick={() =>
                                            toast.error(
                                                'Feature temporarily disabled. Please contact support.'
                                            )
                                        }
                                    >
                                        I understand, disable my account
                                    </Button>{' '}
                                </div>
                            </div>
                        </div>
                    )}
                    {viewMode === 'billing' && (
                        <div className={styles.billingPage}>
                            <div className={styles.topCards}>
                                <div className={styles.billingCard}>
                                    <small>Current monthly bill</small>
                                    <span className={styles.billingCardSpan}>0€</span>
                                </div>
                                <div className={styles.billingCard}>
                                    <small>Next payment due</small>
                                    <span className={styles.billingCardSpan}>N/A</span>
                                </div>
                                <div className={styles.billingCard}>
                                    <small>Current plan</small>
                                    <span className={styles.billingCardSpan}>Free</span>
                                </div>
                            </div>
                            <div className={styles.paymentMethods}>
                                <section className={styles.panel} aria-labelledby='billing-methods'>
                                    <h2 id='billing-methods' className={styles.groupTitle}>
                                        Payment Methods
                                    </h2>
                                    <div className={`${styles.panelBody} ${styles.paymentMethodsArea}`}>
                                        <div className={styles.paymentMethodRow}>
                                            <span>
                                                <BsCashCoin size={17} style={{ marginRight: '5px' }} /> Cash
                                            </span>
                                            <Badge pill style={{ fontSize: '13px' }} bg='secondary'>
                                                Disabled
                                            </Badge>
                                        </div>
                                        <div className={styles.paymentMethodRow}>
                                            <span>
                                                <BsBank size={17} style={{ marginRight: '5px' }} />
                                                Bank Transfer
                                            </span>
                                            <Badge pill style={{ fontSize: '13px' }} bg='secondary'>
                                                Disabled
                                            </Badge>
                                        </div>
                                        <div className={styles.paymentMethodRow}>
                                            <span>
                                                <FaRegCreditCard size={17} style={{ marginRight: '5px' }} />
                                                Debit Card
                                            </span>
                                            <Badge pill style={{ fontSize: '13px' }} bg='secondary'>
                                                Disabled
                                            </Badge>
                                        </div>
                                    </div>
                                </section>
                                <section className={styles.panel} aria-labelledby='billing-history'>
                                    <h2 id='billing-history' className={styles.groupTitle}>
                                        Payment History
                                    </h2>
                                    <div className={styles.panelBody}>
                                        <Table
                                            columns={paymentColumns}
                                            data={[
                                                {
                                                    'Transaction Id': '#000',
                                                    date: '-',
                                                    Amount: '0.00',
                                                    Status: 'Paid'
                                                }
                                            ]}
                                        ></Table>
                                    </div>
                                </section>
                            </div>
                        </div>
                    )}
                    {viewMode === 'notifications' && (
                        <div className={styles.notificationsPage}>
                            <div className={styles.emailNotificationsArea}>
                                <div className={styles.emailNotificationsHeader}>
                                    Email Notifications
                                </div>
                                <div className={styles.emailNotificationsContent}>
                                    <div className={styles.emailNotificationField}>
                                        <span className={styles.notificationFieldLabel}>
                                            Default notification Email
                                        </span>
                                        <span className={styles.readValue}>{currentUser.email}</span>
                                    </div>
                                    <form onSubmit={handleSubmit(onNotificationFormSubmit)}>
                                        <div className={styles.emailNotificationField}>
                                            <label
                                                className={styles.notificationFieldLabel}
                                                htmlFor='changesToAccount'
                                            >
                                                Changes made to your account
                                            </label>
                                            <input
                                                type='checkbox'
                                                id='changesToAccount'
                                                {...register('changesToAccount')}
                                                defaultChecked={
                                                    currentUser.notifications?.changesToAccount ??
                                                    false
                                                }
                                            />{' '}
                                        </div>
                                        <div className={styles.emailNotificationField}>
                                            <label
                                                className={styles.notificationFieldLabel}
                                                htmlFor='promotionalOffers'
                                            >
                                                Marketing and Promotional Offers
                                            </label>
                                            <input
                                                type='checkbox'
                                                id='promotionalOffers'
                                                {...register('promotionalOffers')}
                                                defaultChecked={
                                                    currentUser.notifications?.promotionalOffers ??
                                                    false
                                                }
                                            />
                                        </div>
                                        <button
                                            type='submit'
                                            className={`${styles.changePasswordFormButton} btn btn-primary`}
                                        >
                                            Submit
                                        </button>
                                    </form>
                                </div>
                            </div>
                            <div className={styles.disableAccountArea}>
                                <div className={styles.header}>Disable Account</div>
                                <div className={styles.disableAccountContent}>
                                    <div>
                                        Disable your account is a non permanent action and can only
                                        be undone by support. If you are sure you want to disable
                                        your account, select the button below.
                                    </div>
                                    <Button variant='outline-danger'>
                                        I understand, disable my account
                                    </Button>{' '}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
